import React from 'react';

export interface BlockRendererProps {
  block: { settings: Record<string, any> };
  config: {
    anaRenk: string;
    aksanRenk: string;
    font: string;
    fontBoyutu: number;
    odemeKosullari?: string;
    gecerlilikNotu?: string;
    altBilgiNotu?: string;
  };
  offer?: any;
  offerType?: 'given' | 'received';
  companyInfo?: any;
}

const formatCurrency = (amount: number | undefined, currency: string = 'TRY'): string => {
  if (typeof amount !== 'number') return '-';
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const TotalsBlockRenderer: React.FC<BlockRendererProps> = ({ block, config, offer }) => {
  const s = block?.settings ?? {};
  const position = s.position === 'left' || s.position === 'center' || s.position === 'full' ? s.position : 'right';
  const showDetails = s.showDetails !== false;

  if (!offer || typeof offer !== 'object') {
    return null;
  }

  const currency = offer.currency != null ? String(offer.currency) : 'TRY';
  const subtotal = typeof offer.subtotal === 'number' ? offer.subtotal : undefined;
  const discountTotal = typeof offer.discountTotal === 'number' ? offer.discountTotal : undefined;
  const additionalDiscountAmount =
    typeof offer.additionalDiscountAmount === 'number' ? offer.additionalDiscountAmount : undefined;
  const discountType = offer.discountType;
  const discountValue = offer.discountValue;
  const vatTotal = typeof offer.vatTotal === 'number' ? offer.vatTotal : undefined;
  const sctTotal = typeof offer.sctTotal === 'number' ? offer.sctTotal : undefined;
  const tevkifatRate = offer.tevkifatRate != null ? String(offer.tevkifatRate) : '';
  const tevkifatAmount = typeof offer.tevkifatAmount === 'number' ? offer.tevkifatAmount : undefined;
  const stopajRate = typeof offer.stopajRate === 'number' ? offer.stopajRate : undefined;
  const stopajAmount = typeof offer.stopajAmount === 'number' ? offer.stopajAmount : undefined;
  const grandTotal =
    typeof offer.grandTotal === 'number'
      ? offer.grandTotal
      : typeof offer.amount === 'number'
        ? offer.amount
        : undefined;

  const fs = `${Math.max(config.fontBoyutu - 1, 10)}px`;

  const wrap: React.CSSProperties =
    position === 'full'
      ? { width: '100%', maxWidth: '100%', marginBottom: '12px' }
      : {
          width: 'auto',
          maxWidth: '360px',
          marginBottom: '12px',
          marginLeft: position === 'right' || position === 'center' ? 'auto' : 0,
          marginRight: position === 'left' || position === 'center' ? 'auto' : 0,
        };

  const cellL: React.CSSProperties = {
    padding: '6px 12px',
    fontSize: fs,
    borderBottom: '1px solid #eee',
    textAlign: 'left' as const,
  };

  const cellR: React.CSSProperties = {
    padding: '6px 12px',
    fontSize: fs,
    borderBottom: '1px solid #eee',
    textAlign: 'right' as const,
  };

  const rows: React.ReactNode[] = [];

  if (showDetails) {
    if (typeof subtotal === 'number') {
      rows.push(
        <tr key="sub">
          <td style={cellL}>Ara Toplam</td>
          <td style={cellR}>
            {formatCurrency(subtotal)} {currency}
          </td>
        </tr>
      );
    }
    if (typeof discountTotal === 'number' && discountTotal > 0) {
      rows.push(
        <tr key="disc">
          <td style={{ ...cellL, color: '#d32f2f' }}>Satır İskontoları</td>
          <td style={{ ...cellR, color: '#d32f2f' }}>
            -{formatCurrency(discountTotal)} {currency}
          </td>
        </tr>
      );
    }
    if (typeof additionalDiscountAmount === 'number' && additionalDiscountAmount > 0) {
      const ekLabel =
        discountType === 'percentage' && typeof discountValue === 'number'
          ? `Ek İskonto (%${discountValue})`
          : 'Ek İskonto';
      rows.push(
        <tr key="ek">
          <td style={{ ...cellL, color: '#d32f2f' }}>{ekLabel}</td>
          <td style={{ ...cellR, color: '#d32f2f' }}>
            -{formatCurrency(additionalDiscountAmount)} {currency}
          </td>
        </tr>
      );
    }
    if (typeof vatTotal === 'number') {
      rows.push(
        <tr key="vat">
          <td style={cellL}>KDV Toplam</td>
          <td style={cellR}>
            {formatCurrency(vatTotal)} {currency}
          </td>
        </tr>
      );
    }
    if (typeof sctTotal === 'number' && sctTotal > 0) {
      rows.push(
        <tr key="sct">
          <td style={cellL}>ÖTV Toplam</td>
          <td style={cellR}>
            {formatCurrency(sctTotal)} {currency}
          </td>
        </tr>
      );
    }
    if (tevkifatRate && typeof tevkifatAmount === 'number' && tevkifatAmount > 0) {
      rows.push(
        <tr key="tev">
          <td style={cellL}>Tevkifat ({tevkifatRate})</td>
          <td style={cellR}>
            -{formatCurrency(tevkifatAmount)} {currency}
          </td>
        </tr>
      );
    }
    if (typeof stopajRate === 'number' && stopajRate > 0 && typeof stopajAmount === 'number') {
      rows.push(
        <tr key="stop">
          <td style={cellL}>Stopaj (%{stopajRate})</td>
          <td style={cellR}>
            -{formatCurrency(stopajAmount)} {currency}
          </td>
        </tr>
      );
    }
  }

  return (
    <div style={wrap}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse' as const,
          fontFamily: `'${config.font}', Arial, sans-serif`,
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
        }}
      >
        <tbody>
          {rows}
          <tr style={{ backgroundColor: config.anaRenk, color: '#fff' }}>
            <td style={{ ...cellL, borderBottom: 'none', fontWeight: 700, color: '#fff' }}>Genel Toplam</td>
            <td style={{ ...cellR, borderBottom: 'none', fontWeight: 800, fontSize: `${config.fontBoyutu}px`, color: '#fff' }}>
              {formatCurrency(grandTotal, currency)} {currency}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default TotalsBlockRenderer;
